import { LocaleService } from "../i18n/locale.service";
import { inject } from '@angular/core';
import { ResolveFn, Router } from '@angular/router';
import { EMPTY, forkJoin, Observable, of, from } from 'rxjs';
import { FootballerAuthService, usesFootballerAuth } from './footballer-auth.service';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';
import { PlayerService } from './player.service';
import { ChatService } from './chat.service';
import { MessageService } from './message.service';
import { StatsService } from './stats.service';
import { PlaySpaceService } from './playspace.service';
import { ClientNotificationService } from './client-notification.service';
import { DelayedSpinnerService } from './delayed-spinner.service';
import { DialogService } from '../../core/dialog/dialog.service';
import { TranslateService } from '@ngx-translate/core';

// Keep the source screen visible until the destination's initial content is ready.
export const supportScreenResolver: ResolveFn<unknown> = route => {
  const locale = inject(LocaleService);
  const players = inject(PlayerService);
  const chats = inject(ChatService);
  const messages = inject(MessageService);
  const stats = inject(StatsService);
  const places = inject(PlaySpaceService);
  const notifications = inject(ClientNotificationService);
  const spinner = inject(DelayedSpinnerService);
  const dialogs = inject(DialogService);
  const translate = inject(TranslateService);
  const router = inject(Router);
  const footballerAuth = inject(FootballerAuthService);
  const accountId = players.peekCurrentPlayer()?.id;
  const path = route.routeConfig.path;
  let request: Observable<any>;
  if (path === 'chat-list') {
    request = chats.getChatPage(null, 30);
  } else if (path === 'chat-settings' || path === 'messages') {
    const chatId = route.params['chatId'] || route.queryParamMap.get('chatId');
    const entityId = route.params['entityId'] || route.queryParamMap.get('entityId');
    if (!chatId && !entityId) { router.navigate(['/chat-list']); return EMPTY; }
    const chat = chatId ? chats.getChatById(chatId, path === 'messages') : chats.getChatForEntityId(entityId);
    request = chat.pipe(switchMap(value => path === 'messages'
      ? messages.fetchChatMessagePage(value.id, null, 100).pipe(map(page => ({chat: value, page})))
      : of({chat: value})));
  } else if (path === 'player/footballer-friends') {
    request = players.getFootballerFriendsStats('', 0, 20, 'matchesTogether', 'desc');
  } else if (path === 'player/:playerId/football-references') {
    request = players.getFootballReferences(route.params['playerId'], '', 0, 20, route.queryParamMap.get('contextEventId'));
  } else if (path === 'player/:playerId/match-history') {
    request = players.getPlayerMatchHistory(route.params['playerId'], 0, 20, false, route.queryParamMap.get('contextEventId'));
  } else if (path === 'profile/:playerId') {
    request = players.getPublicProfile(route.params['playerId'], route.queryParamMap.get('contextEventId'),
      new URLSearchParams(route.fragment || '').get('share')).pipe(
      map(profile => ({profile, unavailable: false})),
      catchError(() => of({profile: null, unavailable: true}))
    );
  } else if (path === 'notifications') {
    request = notifications.prepare();
  } else if (path.startsWith('playspace/')) {
    request = players.getCurrentUserProfile().pipe(switchMap(player => {
      const coordinates = player.location?.coordinates;
      const available = !!player.city?.trim() && coordinates?.length === 2 && coordinates.every(Number.isFinite);
      return (available ? places.getCurrentLocations() : of([])).pipe(switchMap(items => {
        const selected = items.find(item => item.id === route.queryParamMap.get('playSpaceId'));
        return (selected ? places.getSummary(selected.id) : of(null)).pipe(map(summary => ({items, selected, summary})));
      }));
    }));
  } else {
    let selected;
    try {
      selected = JSON.parse(localStorage.getItem('chatPlayer') || 'null');
      if (!selected && route.params['eventDetailMode'] === 'true') selected = JSON.parse(localStorage.getItem('peiForPlayerDetail') || 'null')?.player;
    } catch { selected = null; }
    if (route.params['eventDetailMode'] === 'false' && ['avatar', 'profile', 'account'].includes(route.queryParamMap.get('section'))) selected = null;
    request = (selected ? of(selected) : players.getCurrentUserProfile()).pipe(switchMap(player => {
      if (!player?.id) return EMPTY;
      const registered = !(player.guest || player.managedPlayer || ['GUEST', 'MANAGED_PLAYER'].includes(player.playerType));
      return forkJoin({
        loginMethods: usesFootballerAuth && route.queryParamMap.get('section') === 'account' && player.id === players.getCurrentPlayer()?.id
          ? from(footballerAuth.loginMethods()) : of(null),
        summary: registered ? stats.getPlayerProfileSummary(player.id) : of(null),
        capabilities: registered && player.id !== players.getCurrentPlayer()?.id ? players.getDetailCapabilities(player.id) : of({canRate: false})
      }).pipe(map(({summary, capabilities, loginMethods}) => ({loginMethods, player: {...player, canRate: capabilities.canRate === true, profileStatsSummary: summary}})));
    }));
  }
  const finishPreparation = spinner.acquire();
  const language = path === 'profile/:playerId'
    ? (accountId ? locale.initialize(players.peekCurrentPlayer()) : locale.usePublic(locale.cachedPublicLanguage() || 'en'))
    : players.getCurrentUserProfile().pipe(switchMap(player => locale.initialize(player)));
  return language.pipe(switchMap(() => request), map(data => {
    if (accountId && players.peekCurrentPlayer()?.id !== accountId) throw new Error('Session changed');
    return data;
  }), catchError(error => {
    dialogs.alert({title: translate.instant('common.alert'), text: translate.instant(['messages', 'chat-settings'].includes(path) && [403, 404].includes(error?.status) ? 'chat.accessLost' : 'errors.generic')});
    if (!router.navigated) router.navigate(['/event-list']);
    return EMPTY;
  }), finalize(finishPreparation));
};
